/**
 * Marketplace API client - thin typed wrappers over apiFetch for the
 * /v1/foundational/integrations/* surface.
 *
 * Schema mirrors the backend shapes in
 * apps/api-controller/src/modules/foundational/integration/schemas.ts.
 */

import { apiFetch } from './api';

export type ConnectorCategory =
  | 'identity_sso'
  | 'siem'
  | 'soar'
  | 'itsm'
  | 'crm'
  | 'communication'
  | 'email'
  | 'dspm'
  | 'dam'
  | 'ddr'
  | 'dlp'
  | 'data_discovery'
  | 'data_governance'
  | 'cloud'
  | 'data_warehouse'
  | 'tprm'
  | 'ipaas'
  | 'cookie_consent'
  | 'india_stack';

export type ConnectorTier = 'native' | 'template' | 'ipaas';
export type ConnectorAuthType =
  | 'api_key'
  | 'oauth2'
  | 'basic_auth'
  | 'client_cert'
  | 'sas_token'
  | 'jwt_bearer'
  | 'none';
export type ConnectorStatus =
  | 'draft'
  | 'testing'
  | 'active'
  | 'paused'
  | 'error'
  | 'disabled';
export type ConnectorHealth = 'healthy' | 'degraded' | 'failing' | 'unknown';

export interface ConnectorTemplate {
  id: string;
  platformKey: string;
  displayName: string;
  category: ConnectorCategory;
  tier: ConnectorTier;
  logoUrl: string | null;
  description: string | null;
  authType: ConnectorAuthType;
  configSchema: Record<string, unknown>;
  supportedEvents: string[];
  inboundEvents: string[];
  supportsErasure: boolean;
  isIndiaStack: boolean;
  handlesPii: boolean;
  piiCategories: string[];
  dataResidency: string | null;
  privacyRiskLevel: 'low' | 'medium' | 'high';
  enabled: boolean;
}

export interface ConnectorInstance {
  id: string;
  templateId: string;
  templatePlatformKey: string;
  displayName: string;
  status: ConnectorStatus;
  healthStatus: ConnectorHealth | null;
  config: Record<string, unknown>;
  eventSubscriptions: string[];
  supportsErasure: boolean;
  inboundEnabled: boolean;
  dataDirection: 'outbound' | 'inbound' | 'bidirectional';
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  consecutiveFailures: number;
  createdAt: string;
  updatedAt: string;
}

export interface ListTemplatesResponse {
  templates: ConnectorTemplate[];
  pagination: { total: number; limit: number; offset: number };
}

export interface ListConnectorsResponse {
  connectors: ConnectorInstance[];
}

export interface DispatchOutcome {
  connectorId: string;
  platformKey: string;
  status: 'success' | 'failed' | 'retrying' | 'dead_letter';
  httpStatus: number | null;
  attemptNumber: number;
  durationMs: number;
  errorMessage: string | null;
}

export interface InstallConnectorPayload {
  templatePlatformKey: string;
  displayName: string;
  config: Record<string, unknown>;
  eventSubscriptions: string[];
  dataDirection?: 'outbound' | 'inbound' | 'bidirectional';
}

const BASE = '/v1/foundational/integrations';

export async function listTemplates(query: {
  category?: ConnectorCategory;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<ListTemplatesResponse> {
  const params = new URLSearchParams();
  if (query.category) params.set('category', query.category);
  if (query.search) params.set('search', query.search);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  const qs = params.toString();
  return apiFetch<ListTemplatesResponse>(`${BASE}/templates${qs ? '?' + qs : ''}`);
}

export async function getTemplate(platformKey: string): Promise<ConnectorTemplate> {
  return apiFetch<ConnectorTemplate>(`${BASE}/templates/${encodeURIComponent(platformKey)}`);
}

export async function listConnectors(): Promise<ListConnectorsResponse> {
  return apiFetch<ListConnectorsResponse>(`${BASE}/connectors`);
}

export async function getConnector(connectorId: string): Promise<ConnectorInstance> {
  return apiFetch<ConnectorInstance>(`${BASE}/connectors/${connectorId}`);
}

export async function installConnector(
  payload: InstallConnectorPayload,
): Promise<ConnectorInstance> {
  return apiFetch<ConnectorInstance>(`${BASE}/connectors`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateConnector(
  connectorId: string,
  patch: {
    displayName?: string;
    config?: Record<string, unknown>;
    eventSubscriptions?: string[];
  },
): Promise<ConnectorInstance> {
  return apiFetch<ConnectorInstance>(`${BASE}/connectors/${connectorId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteConnector(connectorId: string): Promise<void> {
  await apiFetch<void>(`${BASE}/connectors/${connectorId}`, { method: 'DELETE' });
}

export async function transitionConnector(
  connectorId: string,
  action: 'activate' | 'pause' | 'disable',
): Promise<ConnectorInstance> {
  return apiFetch<ConnectorInstance>(`${BASE}/connectors/${connectorId}/${action}`, {
    method: 'POST',
  });
}

export async function testConnector(connectorId: string): Promise<DispatchOutcome> {
  return apiFetch<DispatchOutcome>(`${BASE}/connectors/${connectorId}/test`, {
    method: 'POST',
  });
}

export async function oauthInitiate(
  connectorId: string,
  payload: { redirectUri: string; returnUrl?: string },
): Promise<{ authorizeUrl: string; state: string }> {
  return apiFetch(`${BASE}/connectors/${connectorId}/oauth/initiate`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Category metadata for the UI. Used by the category-chip filter and
 * the "Connected" group header. Order here = display order.
 */
export const CATEGORY_META: ReadonlyArray<{
  key: ConnectorCategory;
  label: string;
  description: string;
}> = [
  { key: 'identity_sso', label: 'Identity & SSO', description: 'SAML, OIDC, SCIM, LDAP' },
  { key: 'siem', label: 'SIEM', description: 'Splunk, Sentinel, QRadar, Elastic, Datadog' },
  { key: 'soar', label: 'SOAR', description: 'XSOAR, Splunk SOAR, Tines, Torq' },
  { key: 'itsm', label: 'ITSM', description: 'ServiceNow, Jira, Linear, Freshservice' },
  { key: 'crm', label: 'CRM', description: 'Salesforce, HubSpot, Zoho' },
  { key: 'communication', label: 'Communication', description: 'Slack, Teams, Email, SMS' },
  { key: 'email', label: 'Email', description: 'SMTP, Resend, MS Graph, Google' },
  { key: 'dspm', label: 'DSPM', description: 'Purview, BigID, Cyera, Securiti, Sentra' },
  { key: 'dam', label: 'DAM', description: 'Database activity monitoring' },
  { key: 'ddr', label: 'DDR', description: 'Data detection and response' },
  { key: 'dlp', label: 'DLP', description: 'Data loss prevention' },
  { key: 'data_discovery', label: 'Data Discovery', description: 'Cohesity, OneTrust DD' },
  { key: 'data_governance', label: 'Data Governance', description: 'Collibra, Alation' },
  { key: 'cloud', label: 'Cloud', description: 'AWS Security Hub, GCP SCC, Azure Defender' },
  { key: 'data_warehouse', label: 'Data Warehouse', description: 'Snowflake, BigQuery, Redshift' },
  { key: 'tprm', label: 'TPRM', description: 'OneTrust TPRM, Black Kite' },
  { key: 'ipaas', label: 'iPaaS', description: 'Workato, Tray, Zapier' },
  { key: 'cookie_consent', label: 'Cookie Consent', description: 'OneTrust, Cookiebot' },
  { key: 'india_stack', label: 'India Stack', description: 'Aadhaar, DigiLocker, AA' },
];
